import { createAsyncThunk } from '@reduxjs/toolkit';
import { apiClient } from 'api/apiClient';
import * as Cognito from 'aws-amplify/auth';
import { messagingService } from 'services/messagingService';
import { sseService } from 'services/sseService';
import { userService } from 'services/userService';
import { AppState, appActions } from 'store/app/slice';

const basePath = 'auth/';

enum AuthPaths {
  SIGN_IN = 'signIn',
  INITIAL_SIGN_IN = 'initialSignIn',
  UPDATE_PASSWORD = 'updatePassword',
  RESET_PASSWORD = 'resetPassword',
  CONFIRM_RESET_PASSWORD = 'confirmResetPassword',
  GET_CURRENT_USER = 'getCurrentUser',
  CHECK_IF_USER_IS_SIGNED_IN = 'checkIfUserIsSignedIn',
  SIGN_OUT = 'signOut',
  ON_SIGN_IN = 'onSignIn',
  ON_SIGN_OUT = 'doOnSignOut',
}

const getActionName = (path: AuthPaths) => basePath + path;

type SignInParams = {
  username: string;
  password: string;
};

type UpdatePasswordParams = {
  userId: string;
} & Cognito.UpdatePasswordInput;

type ConfirmPasswordResetProps = {
  email: string;
  confirmationCode: string;
  newPassword: string;
};

const onVisibilityChange = () => {
  if (document.visibilityState === 'hidden') {
    sseService.closeConnection();
  }
};

const onBeforeUnload = () => {
  sseService.closeConnection();
};

const onSignIn = createAsyncThunk(getActionName(AuthPaths.ON_SIGN_IN), async (_, thunkAPI) => {
  await apiClient.setToken();

  messagingService.registerMessageListener(); // firebase notifications
  sseService.initSseService(thunkAPI);

  window.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('beforeunload', onBeforeUnload);
});

const onSignOut = createAsyncThunk(getActionName(AuthPaths.ON_SIGN_OUT), async (_, thunkAPI) => {
  const {
    app: { sseConnected },
  } = thunkAPI.getState() as {
    app: AppState;
  };
  // messagingService.removeToken(); //ubs-46 allow user receive notifications after sign out
  sseConnected && sseService.closeConnection();
  thunkAPI.dispatch(appActions.setSSEConnected(false));
  window.removeEventListener('visibilitychange', onVisibilityChange);
  window.removeEventListener('beforeunload', onBeforeUnload);
});

const signIn = createAsyncThunk(
  getActionName(AuthPaths.SIGN_IN),
  async ({ username, password }: SignInParams, thunkAPI) => {
    await Cognito.signIn({
      username,
      password,
    });

    await thunkAPI.dispatch(onSignIn());
  },
);

// only use is to confirm signIn during password setup
const initialSignIn = createAsyncThunk(
  getActionName(AuthPaths.INITIAL_SIGN_IN),
  async ({ username, password }: SignInParams) => {
    await Cognito.signIn({
      username,
      password,
    });

    await Cognito.confirmSignIn({ challengeResponse: password });
  },
);

const updatePassword = createAsyncThunk(
  getActionName(AuthPaths.UPDATE_PASSWORD),
  async ({ oldPassword, newPassword, userId }: UpdatePasswordParams, thunkAPI) => {
    await Cognito.updatePassword({ oldPassword, newPassword });
    await userService.verifyUser(userId);
    await thunkAPI.dispatch(onSignIn()); // setToken must be after verifyUser
    return { isSuccess: true };
  },
);

const resetPassword = createAsyncThunk(getActionName(AuthPaths.RESET_PASSWORD), async (email: string) => {
  await Cognito.resetPassword({ username: email });
});

const confirmResetPassword = createAsyncThunk(
  getActionName(AuthPaths.CONFIRM_RESET_PASSWORD),
  async ({ email, confirmationCode, newPassword }: ConfirmPasswordResetProps) => {
    return await Cognito.confirmResetPassword({ username: email, confirmationCode, newPassword });
  },
);

const getCurrentUser = createAsyncThunk(getActionName(AuthPaths.GET_CURRENT_USER), async () => {
  return await userService.getCurrentUser();
});

const checkIfUserIsSignedIn = createAsyncThunk(
  getActionName(AuthPaths.CHECK_IF_USER_IS_SIGNED_IN),
  async (_, thunkAPI) => {
    const authSession = await Cognito.fetchAuthSession();

    if (authSession.tokens?.accessToken.toString()) {
      await thunkAPI.dispatch(onSignIn());
      return thunkAPI.fulfillWithValue(true);
    } else {
      await thunkAPI.dispatch(onSignOut());
      return thunkAPI.rejectWithValue(false);
    }
  },
);

const signOut = createAsyncThunk(getActionName(AuthPaths.SIGN_OUT), async (_, thunkAPI) => {
  await Cognito.signOut();
  await thunkAPI.dispatch(onSignOut());
});

export const asyncAuthActions = {
  signIn,
  initialSignIn,
  updatePassword,
  getCurrentUser,
  checkIfUserIsSignedIn,
  signOut,
  resetPassword,
  confirmResetPassword,
};
